import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BottomSheet } from "wiz-front-shared/components/bottomSheet/BottomSheet";
import { getCountryCallingCode, type Country } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import './SelectCountryBox.css';

interface CountryOption {
    value?: Country;
    label: string;
}

interface SelectCountryBoxProps {
    value?: Country;
    onChange: (country: Country) => void;
    options: CountryOption[];
    disabled?: boolean;
    readOnly?: boolean;
}

export const SelectCountryBox = ({ value, onChange, options, disabled, readOnly }: SelectCountryBoxProps) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    // 진입 시 search input auto-focus 하지 않음 — 사용자는 우선 "탐색"을 원함.
    //   auto-focus 시 키보드가 즉시 올라와 정작 보고 싶은 목록을 가림. 검색이 필요한 사용자는 직접 탭.
    //   (탭 후 키보드 열리면 BottomSheet 가 visualViewport 추적으로 위로 올라가서 안 가려짐)

    // 현재 선택된 국가 정보
    const FlagComponent = value && flags[value] ? flags[value] : undefined;

    // 검색 필터링
    const filteredOptions = useMemo(() => {
        if (!searchQuery.trim()) return options.filter(opt => opt.value);
        const query = searchQuery.toLowerCase();
        return options.filter(opt =>
            opt.value && opt.label.toLowerCase().includes(query)
        );
    }, [options, searchQuery]);

    const handleSelect = (country: Country) => {
        onChange(country);
        setIsOpen(false);
        setSearchQuery("");
    };

    const handleOpen = () => {
        if (disabled || readOnly) return;
        setIsOpen(true);
    };

    return (
        <>
            <button
                type="button"
                className="select-country-box-button"
                onClick={handleOpen}
                disabled={disabled}
            >
                {FlagComponent !== undefined && (
                    <span className="select-country-box-flag">
                        <FlagComponent title="" />
                    </span>
                )}
                {value && (
                    <span className="select-country-box-code">
                        +{getCountryCallingCode(value)}
                    </span>
                )}
                <span className="select-country-box-arrow" />
            </button>

            <BottomSheet isOpen={isOpen} setIsOpen={setIsOpen}>
                <div className="select-country-box-sheet">
                    <div className="select-country-box-search-wrapper">
                        <input
                            type="text"
                            className="select-country-box-search"
                            placeholder={t("common.searchCountry")}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="select-country-box-list">
                        {filteredOptions.length === 0 ? (
                            <div className="select-country-box-empty">
                                {t("common.noCountryFound")}
                            </div>
                        ) : (
                            filteredOptions.map((option) => {
                                if (!option.value) return null;
                                const OptionFlag = flags[option.value];
                                const isSelected = option.value === value;
                                return (
                                    <div
                                        key={option.value}
                                        className={`select-country-box-item ${isSelected ? "selected" : ""}`}
                                        onClick={() => handleSelect(option.value!)}
                                    >
                                        <span className="select-country-box-item-flag">
                                            {OptionFlag !== undefined && <OptionFlag title="" />}
                                        </span>
                                        <span className="select-country-box-item-name">
                                            {option.label}
                                        </span>
                                        <span className="select-country-box-item-code">
                                            +{getCountryCallingCode(option.value)}
                                        </span>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </BottomSheet>
        </>
    );
};
